<template>
    <div class="create-instrument">
      <h1>Create an Instrument</h1>
      <form @submit.prevent="submitForm">
        <div class="form-group">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model="instrument.name"
            @input="validateName"
            :class="{ 'is-invalid': validationErrors.includes('name') }"
            required
            class="form-control"
          />
          <div v-if="validationErrors.includes('name')" class="error-feedback">
            Name is required.
          </div>
        </div>
        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            v-model="instrument.description"
            rows="3"
            class="form-control"
          ></textarea>
        </div>
        <div class="form-group">
          <label for="group">Group</label>
          <input
            type="text"
            id="group"
            v-model="instrument.group"
            list="instrument-group-options"
            class="form-control"
          />
          <datalist id="instrument-group-options">
            <option v-for="group in groupOptions" :key="group" :value="group"></option>
          </datalist>
        </div>
        <div class="form-group">
          <label for="serial">ID</label>
          <input
            type="text"
            id="serial"
            v-model="instrument.serial"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="status">Status</label>
          <input
            type="text"
            id="status"
            v-model="instrument.status"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label for="c_calibration_time">Calibration Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="c_calibration_time"
              v-model="instrument.c_calibration_time"
              @change="validateCalibrationTime"
              :class="{ 'is-invalid': validationErrors.includes('c_calibration_time') }"
              :disabled="timeNotApplicable.c_calibration_time"
              class="form-control"
            />
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.c_calibration_time"
                @change="handleNotApplicableToggle('c_calibration_time')"
              />
              <span>N/A</span>
            </label>
          </div>
          <div v-if="validationErrors.includes('c_calibration_time')" class="error-feedback">
            Calibration Time is required.
          </div>
        </div>
        <div class="form-group">
          <label for="n_calibration_time">Next Calibration Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="n_calibration_time"
              v-model="instrument.n_calibration_time"
              @change="validateNextCalibrationTime"
              :class="{ 'is-invalid': validationErrors.includes('n_calibration_time') }"
              :disabled="timeNotApplicable.n_calibration_time"
              class="form-control"
            />
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.n_calibration_time"
                @change="handleNotApplicableToggle('n_calibration_time')"
              />
              <span>N/A</span>
            </label>
          </div>
          <div v-if="validationErrors.includes('n_calibration_time')" class="error-feedback">
            Next Calibration Time is required.
          </div>
        </div>
        <div class="form-group">
          <label for="c_maintenance">Maintenance Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="c_maintenance"
              v-model="instrument.c_maintenance"
              :disabled="timeNotApplicable.c_maintenance"
              class="form-control"
            />
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.c_maintenance"
                @change="handleNotApplicableToggle('c_maintenance')"
              />
              <span>N/A</span>
            </label>
          </div>
        </div>
        <div class="form-group">
          <label for="n_maintenance">Next Maintenance Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="n_maintenance"
              v-model="instrument.n_maintenance"
              :disabled="timeNotApplicable.n_maintenance"
              class="form-control"
            />
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.n_maintenance"
                @change="handleNotApplicableToggle('n_maintenance')"
              />
              <span>N/A</span>
            </label>
          </div>
        </div>
        <div class="form-group">
          <label for="user_id">Responsible User</label>
          <select
            v-model="instrument.user_id"
            class="form-control"
          >
            <option value="">Select User</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.username }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="location_id">Location</label>
          <select
            v-model="instrument.location_id"
            @change="validateLocation"
            :class="{ 'is-invalid': validationErrors.includes('location_id') }"
            required
            class="form-control"
          >
            <option value="">Select Location</option>
            <option v-for="location in locations" :key="location.id" :value="location.id">
              {{ location.name }}
            </option>
          </select>
          <div v-if="validationErrors.includes('location_id')" class="error-feedback">
            Location is required.
          </div>
        </div>
        <div class="form-group">
          <label for="rental_place_id">Support Expenses</label>
          <select id="rental_place_id" v-model="instrument.rental_place_id" class="form-control">
            <option :value="null">None</option>
            <option v-for="rentalPlace in rentalPlaces" :key="rentalPlace.id" :value="rentalPlace.id">
              {{ rentalPlace.cost_number }}
            </option>
          </select>
        </div>
        <button type="submit" class="submit-button" :disabled="loading">
          <span v-if="loading" class="spinner"></span>
          <span v-else>Create Instrument</span>
        </button>
      </form>
      <div v-if="loading" class="overlay">
        <div class="spinner"></div>
      </div>
    </div>
  </template>
  
  <script>
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import axios from "axios";

const FORM_CACHE_KEY = "instrument_form";
const CACHE_EXPIRATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export default {
  data() {
    return {
      instrument: {
        name: '',
        serial: '',
        status: '',
        description: '',
        group: '',
        c_calibration_time: '',
        n_calibration_time: '',
        c_maintenance: '',
        n_maintenance: '',
        location_id: null,
        rental_place_id: null,
        user_id: '',
      },
      validationErrors: [],
      loading: false,
      locations: [],
      rentalPlaces: [],
      groupOptions: [],
      users: [],
      timeNotApplicable: {
        c_calibration_time: false,
        n_calibration_time: false,
        c_maintenance: false,
        n_maintenance: false,
      },
    };
  },
  computed: {

  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  async created() {
    this.loadCachedFormData();
    await this.fetchLocations();
    this.fetchUsers();
    this.fetchRentalPlaces();
    this.fetchGroupOptions();
  },
  methods: {
    // Load cached form data if available and not expired
    loadCachedFormData() {
      const cachedData = JSON.parse(localStorage.getItem(FORM_CACHE_KEY));
      if (cachedData) {
        const { data, timestamp, timeNotApplicable } = cachedData;
        const now = Date.now();

        if (now - timestamp < CACHE_EXPIRATION) {
          this.instrument = {
            ...this.instrument,
            ...(data || {}),
          };
          this.timeNotApplicable = {
            ...this.timeNotApplicable,
            ...(timeNotApplicable || {}),
          };
        } else {
          localStorage.removeItem(FORM_CACHE_KEY); // Expired data
        }
      }
    },

    // Save form data to cache
    cacheFormData() {
      const cache = {
        data: this.instrument,
        timeNotApplicable: this.timeNotApplicable,
        timestamp: Date.now(),
      };
      localStorage.setItem(FORM_CACHE_KEY, JSON.stringify(cache));
    },

    async fetchLocations() {
      try {
        const response = await apiService.get(API_ENDPOINTS.locations);
        this.locations = response;
      } catch (error) {
        this.toast.error("Failed to fetch locations.");
        console.error("Error fetching locations:", error);
      }
    },

    fetchUsers() {
      axios.get(API_ENDPOINTS.users)
        .then(response => {
          this.users = response.data;
        })
        .catch(error => {
          console.error('Failed to fetch users:', error);
        });
    },

    async fetchRentalPlaces() {
      try {
        const response = await axios.get(API_ENDPOINTS.rentalPlaces);
        this.rentalPlaces = response.data;
      } catch (error) {
        console.error('Failed to fetch rental places:', error);
      }
    },

    async fetchGroupOptions() {
      try {
        const response = await axios.get(API_ENDPOINTS.allInstruments);
        this.groupOptions = [...new Set((response.data || []).map(item => item.group).filter(Boolean))].sort();
      } catch (error) {
        console.error('Failed to fetch instrument groups:', error);
      }
    },

    validateName() {
      this.validateField('name');
    },
    validateCalibrationTime() {
      this.validateField('c_calibration_time');
    },
    validateNextCalibrationTime() {
      this.validateField('n_calibration_time');
    },
    validateLocation() {
      this.validateField('location_id');
    },
    handleNotApplicableToggle(field) {
      if (this.timeNotApplicable[field]) {
        this.instrument[field] = '';
      }
      this.validateField(field);
    },
    validateField(field) {
      if (this.timeNotApplicable[field]) {
        this.removeValidationError(field);
        return;
      }

      if (!this.instrument[field]) {
        this.addValidationError(field);
      } else {
        this.removeValidationError(field);
      }
    },
    addValidationError(field) {
      if (!this.validationErrors.includes(field)) {
        this.validationErrors.push(field);
        this.toast.error(`${field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')} is required.`);
      }
    },
    removeValidationError(field) {
      this.validationErrors = this.validationErrors.filter(error => error !== field);
    },
    cancelSelection() {
      this.selectedFiles = [];
      this.$refs.fileInput.value = '';
    },
    validateForm() {
      this.validationErrors = [];

      // Validate all required fields
      const requiredFields = ['name', 'c_calibration_time', 'n_calibration_time', 'location_id'];
      requiredFields.forEach(field => {
        this.validateField(field);
      });

      return this.validationErrors.length === 0;
    },
    async submitInstrumentDetails() {
      if (!this.validateForm()) {
        this.toast.error("Form is invalid!");
        return;
      }

      const instrumentDetails = {
        name: this.instrument.name,
        description: this.instrument.description?.trim() || null,
        group: this.instrument.group?.trim() || null,
        serial: this.instrument.serial,
        status: this.instrument.status,
        c_calibration_time: this.timeNotApplicable.c_calibration_time || !this.instrument.c_calibration_time ? null : this.instrument.c_calibration_time,
        n_calibration_time: this.timeNotApplicable.n_calibration_time || !this.instrument.n_calibration_time ? null : this.instrument.n_calibration_time,
        c_maintenance: this.timeNotApplicable.c_maintenance || !this.instrument.c_maintenance ? null : this.instrument.c_maintenance,
        n_maintenance: this.timeNotApplicable.n_maintenance || !this.instrument.n_maintenance ? null : this.instrument.n_maintenance,
        location_id: this.instrument.location_id,
        rental_place_id: this.instrument.rental_place_id ?? null,
        user_id: this.instrument.user_id,
      };

      try {
        const response = await apiService.createResource(API_ENDPOINTS.instruments, instrumentDetails);
        this.toast.success("Instrument created successfully!");

        localStorage.removeItem(FORM_CACHE_KEY); // Clear cache after submission
        this.$router.push({ name: 'InstrumentDetailPage', params: { id: response.id } });
      } catch (error) {
        this.toast.error(error?.response?.data?.message || "Failed to create instrument.");
        console.error("Error creating instrument:", error);
      }
    },
    async submitForm() {
      this.loading = true;
      await this.submitInstrumentDetails();
      this.loading = false;
    },
  },
  watch: {
    // Automatically cache form data when it changes
    instrument: {
      handler() {
        this.cacheFormData();
      },
      deep: true,
    },
  },
};
</script>

  
  <style scoped lang="scss">
  @import "@/assets/main.scss";
  
  .create-instrument {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: $secondary-color;
    border-radius: 8px;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  
    h1 {
      margin-bottom: 2rem;
      text-align: center;
    }
  }

  .na-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 6px 0 10px;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .datetime-field-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .datetime-field-row .form-control {
    flex: 1;
  }

  .na-toggle--inline {
    margin: 0;
    flex-shrink: 0;
  }
  </style>
  